import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { validateToken, type ValidateTokenResponse } from "../actions/validate-token.actions";

type UseTokenArgs = {
  checkById?: boolean;
  checkByParam?: boolean;
};

export const useToken = ({ checkById = false, checkByParam = false }: UseTokenArgs = {}) => {
  const { token: tokenParam } = useParams();
  const tokenLocal = sessionStorage.getItem("token");
  const [searchParams] = useSearchParams();
  const queryToken = searchParams.get("token");

  const accessToken = useMemo(() => {
    if (tokenLocal) return tokenLocal;

    if (checkByParam && queryToken) return queryToken;

    if (checkById && tokenParam) return tokenParam;

    return null;
  }, [checkById, checkByParam, tokenParam, queryToken, tokenLocal]);

  const forbiddenPathsForTokenUser = useMemo(
    () => ["reset", "join", "signin", "signup", "activation", "deactivation", "connectacleaner", "jobs", "invitation", "flow-hub"],
    [],
  );

  const { pathname } = useLocation();
  const [, indexPath] = pathname.split("/");
  const navigate = useNavigate();

  const query = useQuery<ValidateTokenResponse>({
    queryKey: ["token-validate", accessToken, checkById, checkByParam],
    enabled: !!accessToken,
    retry: false,
    queryFn: () =>
      validateToken({
        token: accessToken as string,
        checkById,
        checkByParam,
      }),
  });

  const isAllowedWithoutToken = !accessToken && forbiddenPathsForTokenUser.includes(indexPath);

  const isValidToken: boolean = query.data?.isValid ?? false;

  return {
    user: query.data?.user,
    accessToken,
    isValidToken,
    isAllowedWithoutToken,
    isLoading: query.isLoading,
    error: query.error,
    data: query.data,
  };
};
